import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { safeStorage } from "@/lib/safeStorage";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext({ user: null, loading: true, login: () => {}, logout: async () => {}, setUser: () => {} });

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const exchangeSession = async (sessionId) => {
  const res = await fetch(`${API}/api/auth/session`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) throw new Error("Sign-in failed");
  return res.json();
};

const detail = async (res, fallback) => {
  const body = await res.json().catch(() => ({}));
  return typeof body.detail === "string" ? body.detail : fallback;
};

export const requestMagicLink = async (email, next) => {
  const res = await fetch(`${API}/api/auth/magic/request`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, next: next || `${window.location.pathname}${window.location.search}${window.location.hash}` || "/#jobs" }),
  });
  if (!res.ok) throw new Error(await detail(res, "Could not send the sign-in link. Check the address and try again."));
  return res.json();
};

export const verifyMagicLink = async (token) => {
  const res = await fetch(`${API}/api/auth/magic/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error(await detail(res, "This sign-in link is invalid or expired."));
  return res.json();
};

export const fetchCurrentUser = async () => {
  const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
  return res.ok ? res.json() : null;
};

// Opens the sign-in dialog (Google or email link) from anywhere in the app.
export const openAuthDialog = (options = {}) => window.dispatchEvent(new CustomEvent("dcyb:open-auth", { detail: options }));

const AUTH_HINT_KEY = "dcyb:auth-hint";

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((next) => {
    if (next) safeStorage.set(AUTH_HINT_KEY, "1");
    else safeStorage.remove(AUTH_HINT_KEY);
    setUserState(next);
  }, []);

  const checkAuth = useCallback(async () => {
    if (safeStorage.get(AUTH_HINT_KEY) === null) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      setUser(res.ok ? await res.json() : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    // If returning from OAuth, AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      setUser(null);
    }
  }, [setUser]);

  const value = useMemo(
    () => ({ user, loading, login: openAuthDialog, logout, setUser }),
    [user, loading, logout, setUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
